import { tg } from "./telegram.js";

/**
 * Who the server thinks we are. Inside Telegram: the signed initData. In a desktop browser during
 * local testing: "?as=42" logs in as dev player 42 (the server only allows this with DEV_LOGIN on).
 */
export function credentials(): string | null {
  if (tg?.initData) return tg.initData;
  const as = new URLSearchParams(location.search).get("as");
  return as ? `dev:${as}:player${as}` : null;
}

/** This player's id, straight from Telegram's login data, so live prices can connect before the profile loads. */
export function playerId(): string | null {
  const id = tg?.initDataUnsafe.user?.id;
  if (id) return String(id);
  return new URLSearchParams(location.search).get("as");
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly detail: Record<string, unknown> = {},
  ) {
    super(code);
  }
}

/** On a stalled mobile connection a request gives up after this long instead of hanging the screen. */
const TIMEOUT_MS = 15_000;

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const auth = credentials();
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(path, {
      method: init.method ?? "GET",
      headers: {
        ...(auth ? { authorization: `tma ${auth}` } : {}),
        ...(init.body !== undefined ? { "content-type": "application/json" } : {}),
      },
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      signal: abort.signal,
    });
  } catch {
    throw new ApiError(0, "network");
  } finally {
    clearTimeout(timer);
  }
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new ApiError(res.status, String(data.error ?? res.status), data);
  return data as T;
}

/** One socket for everything live: arena prices and this player's battle events. Reconnects on its own. */
export function connectLive(onMessage: (channel: string, data: unknown) => void, channels: string[]): () => void {
  let ws: WebSocket | undefined;
  let stopped = false;
  let retry = 1_000;
  const open = () => {
    const auth = credentials();
    if (!auth) return;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    ws = new WebSocket(`${proto}://${location.host}/v1/live?initData=${encodeURIComponent(auth)}`);
    ws.onopen = () => {
      retry = 1_000;
      for (const sub of channels) ws!.send(JSON.stringify({ sub }));
    };
    ws.onmessage = (e) => {
      try {
        const m = JSON.parse(String(e.data)) as { ch: string; data: unknown };
        onMessage(m.ch, m.data);
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onclose = () => {
      if (stopped) return;
      setTimeout(open, retry);
      retry = Math.min(retry * 2, 15_000);
    };
  };
  open();
  return () => {
    stopped = true;
    ws?.close();
  };
}
